import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Globe2,
  Play,
  Loader2,
  Info,
  Map,
  ListOrdered,
  Clock,
  Coins,
  UserPlus,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  Wand2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useJobs } from "@/contexts/JobsContext";
import ConfirmDialog from "@/components/ConfirmDialog";
import CreditEstimate from "@/components/billing/CreditEstimate";
import { detectProvMismatch } from "@/lib/canadianCities";

const POLL_MS = 3000;

export default function ImportRealtorCa() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [location, setLocation] = useState("");
  const [locations, setLocations] = useState([]);
  const [locationId, setLocationId] = useState("");
  const [maxPages, setMaxPages] = useState(30);
  const [importCreditsOk, setImportCreditsOk] = useState(true);
  const [byNameCreditsOk, setByNameCreditsOk] = useState(true);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState(null); // current/last job doc
  const [recentRealtors, setRecentRealtors] = useState([]); // {source: 'realtor_ca'}
  // By-name single import (Feb 2026): user types a full name + city, we
  // hit the same powerai actor with FirstName/LastName filters so
  // realtor.ca returns just the matching agent(s). Auto-enrichment runs
  // in-line inside the same background job so the fresh row lands with
  // socials already populated by the time Scrape History says "done".
  const [byNameQuery, setByNameQuery] = useState("");
  const [byNameLocation, setByNameLocation] = useState("");
  const [byNameAutoEnrich, setByNameAutoEnrich] = useState(true);
  const [byNameRunning, setByNameRunning] = useState(false);
  const isAdmin = user && user.role === "admin";

  // Proactive city→province typo detection. If the user types "Edmonton"
  // in a location that says ", SK" we can tell them BEFORE they burn an
  // Apify credit. Unknown cities skip silently (no false positives).
  const byNameProvMismatch = useMemo(
    () => detectProvMismatch(byNameLocation),
    [byNameLocation],
  );

  useEffect(() => {
    if (user) {
      setLocation(user.location || "Saskatoon, SK");
    }
  }, [user]);

  // Load locations (data-isolation workspaces) — imports are tagged to one
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/locations");
        const locs = data.locations || [];
        setLocations(locs);
        const active = user?.active_location_ids || [];
        const preferred =
          (active.length === 1 && locs.find((l) => l.id === active[0])) || locs[0];
        if (preferred) {
          setLocationId(preferred.id);
          setLocation(preferred.name);
          setByNameLocation(preferred.name);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load most-recent realtor_ca import job + recent realtor_ca realtors
  const loadRecent = async () => {
    try {
      const [jobsRes, realtorsRes] = await Promise.all([
        api.get("/scrape-jobs", { params: { type: "realtor_ca_import", limit: 1 } }),
        api.get("/realtors", { params: { limit: 12, source: "realtor_ca" } }).catch(() => ({ data: [] })),
      ]);
      const j = jobsRes.data?.[0];
      if (j) setJob(j);
      setRecentRealtors((realtorsRes.data || []).filter((r) => r.source === "realtor_ca").slice(0, 12));
    } catch {
      // ignore
    }
  };
  useEffect(() => {
    loadRecent();
  }, []);

  // Post-import prompt: after a realtor.ca job completes with fresh realtors,
  // offer to auto-enrich them. Uses a ref so we only prompt once per finished job.
  const promptedForJobRef = React.useRef(null);
  const [enrichPromptJob, setEnrichPromptJob] = React.useState(null);
  const { track: trackJob } = useJobs();

  // Poll active job
  useEffect(() => {
    if (!job || (job.status !== "running" && job.status !== "queued")) {
      setRunning(false);
      // If the job just finished with new realtors, surface the enrichment prompt
      if (
        job &&
        job.status === "done" &&
        job.id !== promptedForJobRef.current &&
        (job.summary?.new_realtors_created ?? 0) > 0
      ) {
        promptedForJobRef.current = job.id;
        setEnrichPromptJob(job);
      }
      return undefined;
    }
    setRunning(true);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/scrape-jobs/${job.id}`);
        setJob(data);
        if (data.status !== "running" && data.status !== "queued") {
          setRunning(false);
          await loadRecent();
          if (
            data.status === "done" &&
            data.id !== promptedForJobRef.current &&
            (data.summary?.new_realtors_created ?? 0) > 0
          ) {
            promptedForJobRef.current = data.id;
            setEnrichPromptJob(data);
          }
        }
      } catch {
        // ignore
      }
    }, POLL_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poller is keyed on job id + status; the job object itself changes every tick
  }, [job?.id, job?.status]);

  const acceptEnrichNewRealtors = async () => {
    const j = enrichPromptJob;
    setEnrichPromptJob(null);
    if (!j) return;
    try {
      // Kick off an enrich-all restricted to no-socials realtors, capped at
      // the current default 200 Firecrawl credits.
      const { data } = await api.post("/realtors/enrich-all", null, {
        params: { only_missing: true, credit_cap: 200 },
      });
      trackJob(data.job_id, {
        type: "enrich_all",
        label: `${data.targets} realtors (post-import)`,
      });
      toast.success("Enrichment started for new realtors", {
        description: "Banner stays visible while it runs.",
      });
    } catch (e) {
      toast.error("Couldn't start enrichment", { description: formatApiError(e) });
    }
  };

  const startImport = async () => {
    setRunning(true);
    setJob(null);
    try {
      const { data } = await api.post("/system/realtor-ca/import", {
        location: location.trim(),
        location_id: locationId || null,
        max_pages: Number(maxPages) || 30,
      });
      const { data: jobDoc } = await api.get(`/scrape-jobs/${data.job_id}`);
      setJob(jobDoc);
      toast.success("Realtor.ca import started", {
        description: `Agent directory API · Job ${data.job_id.slice(0, 8)}…`,
      });
    } catch (e) {
      setRunning(false);
      toast.error("Failed to start import", { description: formatApiError(e) });
    }
  };

  const startByNameImportWith = async (nameOverride, locationOverride) => {
    const trimmed = (nameOverride ?? byNameQuery).trim();
    if (!trimmed) {
      toast.error("Enter a realtor's name");
      return;
    }
    const city = (locationOverride || byNameLocation || location || "").trim();
    if (!city) {
      toast.error("Location required");
      return;
    }
    setByNameRunning(true);
    try {
      const { data } = await api.post("/system/realtor-ca/import-by-name", {
        name: trimmed,
        location: city,
        location_id: locationId || null,
        auto_enrich: byNameAutoEnrich,
      });
      const { data: jobDoc } = await api.get(`/scrape-jobs/${data.job_id}`);
      setJob(jobDoc);
      toast.success(`Searching realtor.ca for "${trimmed}" in ${city}…`, {
        description: byNameAutoEnrich
          ? "We'll auto-enrich the profile once it's inserted."
          : `Job ${data.job_id.slice(0, 8)}…`,
      });
    } catch (e) {
      toast.error("Failed to start name-search import", {
        description: formatApiError(e),
      });
    } finally {
      setByNameRunning(false);
    }
  };

  const startByNameImport = async () => {
    await startByNameImportWith(byNameQuery, byNameLocation);
    // Clear the name field only on successful submissions (the helper
    // toasts on error) so the user can quickly type the next lookup.
    setByNameQuery("");
  };

  const summary = job?.summary || {};

  return (
    <div className="space-y-5" data-testid="import-realtorca-page">
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">
          Acquisition · Phase 2 source
        </p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 font-display">
          Import from Realtor.ca
        </h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Searches Realtor.ca and pulls realtor cards for a city. Each card
          attempts to fill → name, title, brokerage, phone, photo, personal
          website link, social URLs, and Realtor.ca profile URL. Extra info
          will be added in Step 2.
        </p>
      </div>

      {/* Import form */}
      <section
        className="rounded-lg border border-slate-200 bg-white p-5"
        data-testid="import-realtorca-form"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-md bg-slate-100 p-2 shrink-0">
            <Globe2 className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900 font-display">
              Start a new import
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Enter your city below and the number of pages you want to search (20 agents per page).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Location
            </Label>
            <div className="relative">
              <Map className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Saskatoon, SK"
                className="pl-9 min-h-[44px] mt-1"
                data-testid="import-location-input"
              />
            </div>
            {locations.length > 1 ? (
              <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="import-location-tags">
                <span className="text-[11px] text-slate-500">Tag imports to:</span>
                {locations.map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => { setLocationId(loc.id); setLocation(loc.name); }}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      locationId === loc.id
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                    }`}
                    data-testid={`import-location-tag-${loc.id}`}
                  >
                    {loc.name}
                  </button>
                ))}
              </div>
            ) : null}
            <p className="text-[11px] text-slate-500 mt-1">
              City, province code (e.g. <code>Saskatoon, SK</code>). Province
              code maps to Realtor.ca's internal id.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Max pages (20 agents/page · up to 50 pages ≈ 1,000 agents)
            </Label>
            <div className="relative">
              <ListOrdered className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                type="number"
                min={1}
                max={50}
                value={maxPages}
                onChange={(e) => setMaxPages(e.target.value)}
                className="pl-9 min-h-[44px] mt-1"
                data-testid="import-maxpages-input"
              />
            </div>
          </div>
        </div>

        <div
          className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600"
          data-testid="import-engine-note"
        >
          Pulls the full agent directory straight from Realtor.ca&apos;s internal
          agent-search API via a pay-per-result Apify actor (~$5 per 1,000 agents).
          Fills name, title, brokerage, phone, photo, personal website and profile
          link. No browser, no bot-wall timeouts.
        </div>
        <CreditEstimate action="realtor_import" params={{ max_pages: Number(maxPages) || 30 }} onAllowed={setImportCreditsOk} className="mt-3" />

        <div className="flex flex-wrap gap-2 mt-5">
          <Button
            onClick={startImport}
            disabled={running || !importCreditsOk}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="start-realtorca-import-button"
            title="Start pulling realtor cards for the city + page count above"
          >
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {running ? "Importing…" : "Start import"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => navigate("/scrape-history")}
            className="min-h-[44px]"
          >
            <Clock className="h-4 w-4 mr-2" /> Scrape history
          </Button>
        </div>
      </section>

      {/* By-name single import — cheap fallback for adding one specific
          realtor without pulling the whole city again. Fires the same
          powerai actor with FirstName+LastName+City filters and (default
          on) auto-enriches the fresh row so socials land with it. */}
      <section
        className="rounded-lg border border-slate-200 bg-white p-5"
        data-testid="import-realtorca-by-name-form"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="rounded-md bg-emerald-50 p-2 shrink-0">
            <UserPlus className="h-4 w-4 text-emerald-700" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900 font-display">
              Or add a single realtor by name
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              Types the name + city into Realtor.ca's agent search, inserts the
              match into your list, and — if left on — runs enrichment on it
              automatically so their socials are populated before you open
              the row. Costs pennies (5-25 cards ≈ $0.03).
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Realtor name
            </Label>
            <div className="relative">
              <UserPlus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={byNameQuery}
                onChange={(e) => setByNameQuery(e.target.value)}
                placeholder="e.g. Aaron Wright"
                className="pl-9 min-h-[44px] mt-1"
                data-testid="import-byname-name-input"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !byNameRunning) {
                    e.preventDefault();
                    startByNameImport();
                  }
                }}
              />
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              First + last name. Middle names collapse into the FirstName filter.
            </p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">
              Location
            </Label>
            <div className="relative">
              <Map className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                value={byNameLocation}
                onChange={(e) => setByNameLocation(e.target.value)}
                placeholder="Saskatoon, SK"
                className={`pl-9 min-h-[44px] mt-1 ${
                  byNameProvMismatch ? "border-amber-400 focus-visible:ring-amber-300" : ""
                }`}
                data-testid="import-byname-location-input"
              />
            </div>
            {byNameProvMismatch ? (
              <div
                className="mt-1 flex items-start gap-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5"
                data-testid="import-byname-prov-mismatch-hint"
              >
                <Wand2 className="h-3 w-3 mt-0.5 shrink-0 text-amber-600" />
                <span className="flex-1">
                  {byNameProvMismatch.city} is usually{" "}
                  <strong>{byNameProvMismatch.expectedProv}</strong>, not{" "}
                  <strong>{byNameProvMismatch.typedProv}</strong>.
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setByNameLocation(
                      `${byNameProvMismatch.city}, ${byNameProvMismatch.expectedProv}`,
                    )
                  }
                  className="font-semibold text-amber-900 hover:text-amber-950 underline underline-offset-2 shrink-0"
                  data-testid="import-byname-prov-fix-button"
                >
                  Fix to {byNameProvMismatch.expectedProv}
                </button>
              </div>
            ) : (
              <p className="text-[11px] text-slate-500 mt-1">
                City, province — matches the tag above by default.
              </p>
            )}
          </div>
        </div>

        <label
          className="mt-4 flex items-center gap-2 text-sm text-slate-700 select-none cursor-pointer"
          data-testid="import-byname-autoenrich-label"
        >
          <Checkbox
            checked={byNameAutoEnrich}
            onCheckedChange={(v) => setByNameAutoEnrich(!!v)}
            data-testid="import-byname-autoenrich-checkbox"
          />
          <Sparkles className="h-4 w-4 text-emerald-600" />
          <span>
            Auto-enrich the freshly-imported profile
            <span className="text-xs text-slate-500 ml-1">
              (fills socials + verified photo · adds seconds to the run)
            </span>
          </span>
        </label>
        <CreditEstimate action="import_by_name" params={{}} onAllowed={setByNameCreditsOk} className="mt-3" />

        <div className="flex flex-wrap gap-2 mt-4">
          <Button
            onClick={startByNameImport}
            disabled={byNameRunning || !byNameQuery.trim() || !byNameCreditsOk}
            className="text-white min-h-[44px]"
            style={{ background: "hsl(var(--brand))" }}
            data-testid="start-realtorca-byname-button"
            title="Search Realtor.ca for this specific realtor and add them to your list"
          >
            {byNameRunning ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <UserPlus className="h-4 w-4 mr-2" />
            )}
            {byNameRunning ? "Searching…" : "Search & import"}
          </Button>
          <span className="text-xs text-slate-500 self-center">
            Result lands in your Realtors list — job status shows below.
          </span>
        </div>

        <ByNameJobResult
          job={job}
          onRetry={(name, suggestedLoc) => {
            if (name) setByNameQuery(name);
            if (suggestedLoc) {
              setByNameLocation(suggestedLoc);
              // Auto-fire the retry when we have a concrete "did you mean"
              // suggestion — one-click UX, no second confirmation needed.
              setTimeout(() => {
                startByNameImportWith(name, suggestedLoc);
              }, 50);
            }
          }}
        />
      </section>

      {/* Live job status */}
      {job ? (
        <section
          className="rounded-lg border border-slate-200 bg-white p-5"
          data-testid="import-realtorca-status"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <JobStatusBadge status={job.status} />
              <span className="text-sm text-slate-700 font-medium">
                {job.target_label}
              </span>
            </div>
            <div className="text-xs text-slate-500 tabular-nums">
              {fmtTime(job.started_at)}
              {job.finished_at ? ` → ${fmtTime(job.finished_at)}` : null}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 text-sm">
            <KpiTile label="Pages crawled" value={summary.pages_crawled || 0} />
            <KpiTile label="Realtors found" value={summary.realtors_found || 0} />
            <KpiTile label="New added" value={summary.new_added || 0} accent="emerald" />
            <KpiTile label="Merged" value={summary.merged_into_existing || 0} accent="sky" />
          </div>

          {summary.cap_reached ? (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-2">
              <Info className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium">Requested amount fetched — more agents may be available.</div>
                <div className="mt-0.5">
                  Raise <em>Max pages</em> (up to 50 ≈ 1,000 agents in API mode) and
                  re-run to pull the rest. Already-imported realtors are merged, not duplicated.
                </div>
              </div>
            </div>
          ) : null}

          {/* Admin-only: agents dropped by the strict city gate. Realtor.ca's
              agent-search API loosely matches by service area, so a search
              for e.g. "Regina" can return agents whose brokerage is in
              Saskatoon. We drop those at ingest so per-location data stays
              clean, and surface the list here so an admin can add them
              manually to a different location if they legitimately moved. */}
          {isAdmin && Array.isArray(summary.skipped_out_of_city) && summary.skipped_out_of_city.length > 0 ? (
            <details
              className="mt-3 text-xs bg-slate-50 border border-slate-200 rounded p-3"
              data-testid="import-skipped-out-of-city"
            >
              <summary className="cursor-pointer font-medium text-slate-700">
                Skipped out-of-city agents ({summary.skipped_out_of_city.length})
                <span className="ml-2 font-normal text-slate-500">
                  — realtor.ca returned these for “{summary.location}” but their brokerage is elsewhere
                </span>
              </summary>
              <ul className="mt-2 divide-y divide-slate-200">
                {summary.skipped_out_of_city.map((s, i) => (
                  <li key={i} className="py-1.5" data-testid={`skipped-agent-${i}`}>
                    <div className="font-medium text-slate-900">{s.name}</div>
                    <div className="text-slate-600">
                      {s.brokerage ? <span>{s.brokerage}</span> : null}
                      {s.brokerage && s.address ? <span className="text-slate-400"> · </span> : null}
                      {s.address ? <span>{s.address}</span> : null}
                    </div>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-slate-500">
                If any of these actually work in your target city, add them manually
                from the Realtors page.
              </p>
            </details>
          ) : null}

          {!isAdmin && (summary.skipped_out_of_city_count || 0) > 0 ? (
            <div
              className="mt-3 text-xs text-slate-600 flex items-start gap-2"
              data-testid="import-skipped-out-of-city-count"
            >
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-400" />
              <span>
                {summary.skipped_out_of_city_count} out-of-city agent(s) were skipped by
                the city gate. Ask an admin for the list if you think one of them belongs here.
              </span>
            </div>
          ) : null}

          {Array.isArray(job.errors) && job.errors.length > 0 ? (
            <>
              {(() => {
                const approvalErr = job.errors.find((e) =>
                  /approvePermissions|requires full access|approve its permissions/i.test(e || "")
                );
                const approvalUrl = approvalErr
                  ? (approvalErr.match(/https:\/\/console\.apify\.com\/actors\/[^\s)]+/i) || [null])[0]
                  : null;
                if (approvalUrl) {
                  return (
                    <div
                      className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded p-3"
                      data-testid="apify-approval-banner"
                    >
                      <div className="font-medium mb-1 text-sm">
                        One-time Apify Actor approval required
                      </div>
                      <p className="mb-2 text-amber-800">
                        The <code>apify/web-scraper</code> Actor needs a one-click
                        permission grant on your Apify account before the
                        search-directory mode can run. Open the link below in a
                        new tab, click <em>Approve</em>, then come back here and
                        click <em>Start import</em> again.
                      </p>
                      <a
                        href={approvalUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline font-medium text-amber-900 hover:text-amber-700"
                        data-testid="apify-approval-link"
                      >
                        Approve apify/web-scraper →
                      </a>
                    </div>
                  );
                }
                return null;
              })()}
              <details className="mt-3 text-xs">
                <summary className="cursor-pointer text-slate-600">
                  Errors ({job.errors.length})
                </summary>
                <pre className="bg-slate-50 border border-slate-200 rounded p-2 mt-1 overflow-x-auto">
{job.errors.join("\n")}
                </pre>
              </details>
            </>
          ) : null}

          <div className="mt-3 text-xs text-slate-500 flex items-center gap-2">
            <Coins className="h-3.5 w-3.5" />
            Firecrawl credits used: <span className="tabular-nums">{summary.firecrawl_credits || 0}</span>{" "}
            (this engine doesn't burn Firecrawl)
          </div>
        </section>
      ) : null}

      {/* Recent imported preview */}
      {recentRealtors.length > 0 ? (
        <section
          className="rounded-lg border border-slate-200 bg-white p-5"
          data-testid="import-realtorca-preview"
        >
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h2 className="text-sm font-semibold text-slate-900">
              Recent Realtor.ca cards ({recentRealtors.length})
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/realtors")}
              className="min-h-[44px]"
            >
              View all →
            </Button>
          </div>
          <ul className="divide-y divide-slate-100">
            {recentRealtors.map((r) => (
              <li
                key={r.id}
                className="py-2.5 flex items-start gap-3"
                data-testid={`preview-realtor-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {r.name || `${r.first_name} ${r.last_name}`}
                    {r.title ? <span className="text-slate-500 font-normal"> · {r.title}</span> : null}
                  </div>
                  <div className="text-xs text-slate-600 truncate">{r.brokerage_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                    {r.phone ? <span>{r.phone}</span> : null}
                    {r.realtor_website_url ? (
                      <a
                        href={r.realtor_website_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[hsl(var(--brand-strong))] hover:underline truncate max-w-[16rem]"
                      >
                        Personal site
                      </a>
                    ) : null}
                    {r.profile_url ? (
                      <a
                        href={r.profile_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-slate-500 hover:text-slate-700 hover:underline"
                      >
                        Realtor.ca →
                      </a>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* Post-import enrichment prompt. Fires once when a Realtor.ca import
          finishes with new_realtors_created > 0. Approving kicks off a
          budget-capped /realtors/enrich-all restricted to missing-socials. */}
      <ConfirmDialog
        open={!!enrichPromptJob}
        title="Enrich new realtors now?"
        description={
          enrichPromptJob
            ? `Import added ${enrichPromptJob.summary?.new_realtors_created ?? 0} new realtors. Auto-enrich socials (capped at 200 Firecrawl credits, missing-only) so their Facebook/Instagram/LinkedIn URLs are filled in?`
            : ""
        }
        confirmLabel="Start enrichment"
        onConfirm={acceptEnrichNewRealtors}
        onClose={() => setEnrichPromptJob(null)}
        testid="post-import-enrich-dialog"
      />
    </div>
  );
}

function KpiTile({ label, value, accent = "slate" }) {
  const accentCls =
    accent === "emerald"
      ? "text-emerald-700"
      : accent === "sky"
      ? "text-sky-700"
      : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-bold tabular-nums mt-0.5 ${accentCls}`}>{value}</div>
    </div>
  );
}

function JobStatusBadge({ status }) {
  const cls =
    status === "done"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "failed"
      ? "bg-red-50 text-red-700 border-red-200"
      : status === "running" || status === "queued"
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`px-2 py-0.5 text-xs rounded border inline-flex items-center gap-1 ${cls}`}>
      {status === "running" || status === "queued" ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : null}
      {status}
    </span>
  );
}

function fmtTime(iso) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso || "";
  }
}


/**
 * By-name job result banner — shows a green success block when new
 * realtors were inserted, a red "wrong province" nudge when Realtor.ca
 * returned matches but our province gate dropped them all, and a plain
 * "no results" note otherwise. Only renders for search_by_name jobs
 * (skipped for the bulk directory import which has its own status card).
 */
function ByNameJobResult({ job, onRetry }) {
  if (!job) return null;
  const summary = job.summary || {};
  if (summary.import_mode !== "search_by_name") return null;
  const status = job.status;
  if (status === "queued" || status === "running") {
    return (
      <div
        className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-center gap-2"
        data-testid="import-byname-status-running"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
        <span>
          Searching Realtor.ca for <strong>{summary.name_query || "…"}</strong> in{" "}
          <strong>{summary.location || "…"}</strong>
          {summary.auto_enriched_count ? " · auto-enriching results" : ""}…
        </span>
      </div>
    );
  }
  if (status !== "done") return null;

  const inserted = summary.inserted_ids?.length || 0;
  const merged = summary.merged_into_existing || 0;
  const apifyCount = summary.apify_result_count || 0;
  const skippedProv = summary.skipped_other_prov || [];
  const skippedCity = summary.skipped_out_of_city || [];
  const provFull = summary.location_prov_full || "";

  // Happy path — at least one realtor landed in the list.
  if (inserted > 0 || merged > 0) {
    // Realtor.ca occasionally returns the same agent twice with tiny
    // address-format differences (e.g. "82 Avenue NW" vs "82 Ave NW").
    // Our `_persist_cards` dedupe collapses these correctly, but the
    // raw `matched_cards` list still shows both — which reads to the
    // user like "why is there a second Alex Ward hiding somewhere?".
    // Dedupe the display list by name + brokerage and surface a
    // "Realtor.ca returned N duplicate(s)" note when we collapsed rows.
    const cards = summary.matched_cards || [];
    const seen = new Set();
    const uniqueCards = [];
    for (const c of cards) {
      const key = `${(c.name || "").toLowerCase().trim()}|${(c.brokerage || "").toLowerCase().trim()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      uniqueCards.push(c);
    }
    const duplicateCount = cards.length - uniqueCards.length;
    return (
      <div
        className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
        data-testid="import-byname-result-success"
      >
        <div className="flex items-start gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              {inserted > 0 && merged > 0
                ? `${inserted} new + ${merged} merged for "${summary.name_query}"`
                : inserted > 0
                ? `${inserted} new realtor${inserted > 1 ? "s" : ""} added for "${summary.name_query}"`
                : `${merged} existing realtor${merged > 1 ? "s" : ""} updated for "${summary.name_query}"`}
            </div>
            {summary.auto_enriched_count > 0 ? (
              <div className="text-xs text-emerald-800 mt-0.5">
                Auto-enriched {summary.auto_enriched_count} profile
                {summary.auto_enriched_count > 1 ? "s" : ""} — socials should be
                populated in the Realtors list.
              </div>
            ) : null}
            {duplicateCount > 0 ? (
              <div className="text-xs text-emerald-800 mt-0.5">
                Realtor.ca returned {duplicateCount} duplicate listing
                {duplicateCount > 1 ? "s" : ""} (same agent + brokerage under
                different address formats) — deduplicated automatically.
              </div>
            ) : null}
          </div>
        </div>
        {uniqueCards.length > 0 ? (
          <ul className="mt-2 ml-6 space-y-0.5 text-xs">
            {uniqueCards.slice(0, 5).map((c, i) => (
              <li key={i} className="text-emerald-800">
                <span className="font-medium">{c.name}</span>
                {c.brokerage ? <span> · {c.brokerage}</span> : null}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    );
  }

  // Zero-kept path — most useful case: apify returned rows but our
  // province gate dropped them. Show the wrong-province samples so the
  // user can see WHY nothing landed, plus a "Did you mean?" one-click
  // retry when the backend inferred the correct province from the
  // skipped addresses.
  if (apifyCount > 0 && skippedProv.length > 0) {
    const suggestedLoc = summary.suggested_location;
    const suggestedProvFull = summary.suggested_prov_full;
    return (
      <div
        className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        data-testid="import-byname-result-wrong-prov"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold">
              Realtor.ca returned {skippedProv.length}{" "}
              {skippedProv.length === 1 ? "match" : "matches"} for "
              {summary.name_query}", but{" "}
              {skippedProv.length === 1 ? "it's" : "they're"} outside{" "}
              {provFull || summary.location_prov_code || summary.location}.
            </div>
            {suggestedLoc ? (
              <div className="text-xs text-amber-800 mt-0.5">
                Did you mean{" "}
                <strong className="text-amber-900">{suggestedLoc}</strong>
                {suggestedProvFull ? ` (${suggestedProvFull})` : ""}? The Location
                field currently reads{" "}
                <code className="bg-amber-100 px-1 rounded">{summary.location}</code>.
              </div>
            ) : (
              <div className="text-xs text-amber-800 mt-0.5">
                Try a different province. The Location field currently reads{" "}
                <code className="bg-amber-100 px-1 rounded">{summary.location}</code>.
              </div>
            )}
            <ul className="mt-2 space-y-1 text-xs">
              {skippedProv.slice(0, 5).map((c, i) => (
                <li key={i} className="text-amber-900">
                  <span className="font-medium">{c.name}</span>
                  {c.brokerage ? <span> · {c.brokerage}</span> : null}
                  {c.address ? (
                    <div className="text-amber-700">{c.address}</div>
                  ) : null}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              {suggestedLoc ? (
                <Button
                  size="sm"
                  className="text-white h-8"
                  style={{ background: "hsl(var(--brand))" }}
                  onClick={() => onRetry?.(summary.name_query, suggestedLoc)}
                  data-testid="import-byname-retry-suggested-button"
                >
                  Retry as “{suggestedLoc}”
                </Button>
              ) : null}
              {onRetry ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-400 text-amber-900 hover:bg-amber-100 h-8"
                  onClick={() => onRetry?.(summary.name_query, "")}
                  data-testid="import-byname-retry-button"
                >
                  Refill name — I'll fix the location
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Apify returned nothing at all (bad city, unknown agent, or typo).
  if (apifyCount === 0) {
    return (
      <div
        className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"
        data-testid="import-byname-result-empty"
      >
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium text-slate-900">
              Realtor.ca didn't return any matches for "{summary.name_query}" in{" "}
              {summary.location}.
            </div>
            <div className="text-xs mt-0.5">
              Double-check the spelling, try a different city, or run the bulk
              directory import above and let the roster reveal them.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback — matches existed but all dropped by strict city gate.
  if (skippedCity.length > 0) {
    return (
      <div
        className="mt-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
        data-testid="import-byname-result-wrong-city"
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">
              {skippedCity.length} match(es) found but{" "}
              {skippedCity.length === 1 ? "the brokerage is" : "their brokerages are"}{" "}
              outside {summary.location.split(",")[0].trim()}.
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {skippedCity.slice(0, 5).map((c, i) => (
                <li key={i}>
                  <span className="font-medium">{c.name}</span>
                  {c.brokerage ? <span> · {c.brokerage}</span> : null}
                  {c.address ? <div className="text-amber-700">{c.address}</div> : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
